import Image from 'next/image'

import { clsx } from 'clsx'
import { EffectCards } from 'swiper/modules'
import { SwiperClass, SwiperSlide } from 'swiper/react'

import { FC, useState } from 'react'

import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { ImageLetsGo } from '@/app/shared/images'
import { ITitleEditorList } from '@/app/shared/interfaces/content.interface'
import { ButtonComponent } from '@/app/shared/ui/button'
import { SliderComponent } from '@/app/shared/ui/slider'
import { SliderArrowComponent } from '@/app/shared/ui/slider-arrow'
import { TitleComponent } from '@/app/shared/ui/title'

// interface
interface ISliderMethodology {
  data: ITitleEditorList
}

// component
const SliderMethodologyComponent: FC<Readonly<ISliderMethodology>> = ({ data }) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  const [secondSwiper, setSecondSwiper] = useState<SwiperClass | null>(null)
  const [activeSlide, setActiveSlide] = useState<number>(0)

  const titles = data.titleEditorImage

  const btnArr = titles.map((el) => el.title)

  return (
    <div>
      <div
        className={
          '-ml-[30px] w-[100vw] laptop:mr-[100px] laptop:w-[calc(100vw-440px)] desktop:w-[calc(100vw-313px-100px-440px-26px)]'
        }
      >
        <SliderComponent
          modules={[EffectCards]}
          effect={'cards'}
          cardsEffect={{ slideShadows: false, rotate: false, perSlideOffset: 10 }}
          grabCursor={true}
          centeredSlides={true}
          onSwiper={(e) => setSwiper(e)}
          onSlideChange={() => setActiveSlide(swiper?.activeIndex ?? 0)}
          slidesPerView={1.1}
          spaceBetween={15}
          className={'hidden laptop:flex'}
          breakpoints={{
            1024: {
              slidesPerView: 1.4,
              spaceBetween: 20,
            },
            1280: {
              slidesPerView: 1,
              centeredSlides: false,
              spaceBetween: 0,
            },
          }}
        >
          {titles.map((el, index) => (
            <SwiperSlide key={`${el.title}_${index}`} className={'!h-auto'}>
              <div
                className={
                  'grid h-full gap-10 rounded-large border-2 border-secondary bg-content1-200 p-[40px_50px_36px_50px]'
                }
              >
                <div className={'flex items-center justify-between'}>
                  <TitleComponent size={'md'} className={'text-secondary'}>
                    {el.title}
                  </TitleComponent>
                  <AnimationMoveIconComponent className={'relative h-[93px] w-[100px]'}>
                    <Image
                      src={el.image ?? ImageLetsGo}
                      alt={'slide'}
                      fill
                      className={'object-contain'}
                      sizes={'100px'}
                    />
                  </AnimationMoveIconComponent>
                </div>
                <div
                  className={'text-[18px] laptop:text-[22px]'}
                  dangerouslySetInnerHTML={{ __html: el.description }}
                />
              </div>
            </SwiperSlide>
          ))}
        </SliderComponent>
        <SliderComponent
          grabCursor={true}
          centeredSlides={true}
          onSwiper={(e) => setSecondSwiper(e)}
          onSlideChange={() => setActiveSlide(swiper?.activeIndex ?? 0)}
          slidesPerView={1.1}
          spaceBetween={15}
          className={'flex laptop:hidden'}
          breakpoints={{
            1024: {
              slidesPerView: 1.4,
              spaceBetween: 20,
            },
            1280: {
              slidesPerView: 1,
              centeredSlides: false,
              spaceBetween: 0,
            },
          }}
        >
          {titles.map((el, index) => (
            <SwiperSlide key={`${el.title}_${index}`} className={'!h-auto'}>
              <div
                className={
                  'grid h-full gap-10 rounded-large border-2 border-secondary bg-content1-200 p-[20px_30px_16px_30px] extraMobile:p-[40px_50px_36px_50px]'
                }
              >
                <div className={'flex flex-wrap items-center justify-between'}>
                  <TitleComponent size={'md'} className={'text-secondary'}>
                    {el.title}
                  </TitleComponent>
                  <AnimationMoveIconComponent className={'relative h-[93px] w-[100px]'}>
                    <Image
                      src={el.image ?? ImageLetsGo}
                      alt={'slide'}
                      fill
                      className={'object-contain'}
                    />
                  </AnimationMoveIconComponent>
                </div>
                <div
                  className={'text-[18px] laptop:text-[22px]'}
                  dangerouslySetInnerHTML={{ __html: el.description }}
                />
              </div>
            </SwiperSlide>
          ))}
        </SliderComponent>
      </div>
      <div
        className={'grid items-start gap-[30px] desktop:flex desktop:justify-between desktop:gap-0'}
      >
        <ul className={'flex flex-wrap items-center'}>
          {btnArr.map((str, index) => (
            <li key={index}>
              <ButtonComponent
                color={'secondary'}
                variant={index === activeSlide ? undefined : 'bordered'}
                className={clsx('border-2 text-xl lowercase 2xl:text-[22px]', {
                  'bg-content1-200 text-black': index !== activeSlide,
                  'pointer-events-none': index === activeSlide,
                })}
                size={'lg'}
                onClick={() => {
                  swiper?.slideTo(index)
                  secondSwiper?.slideTo(index)
                }}
              >
                {index + 1}. {str}
              </ButtonComponent>
            </li>
          ))}
        </ul>
        <div
          className={
            'mx-auto hidden items-center tablet:flex desktop:ml-5 desktop:-translate-y-[20px] large:mr-20 large:-translate-y-[59px]'
          }
        >
          <SliderArrowComponent
            button={'prev'}
            className={'mr-5 hover:!border-secondary hover:!bg-secondary'}
            classSize={'h-[80px] w-[80px]'}
            classSizeIcon={'w-[34px] h-[45px]'}
            onClick={() => {
              swiper?.slidePrev()
              secondSwiper?.slidePrev()
            }}
          />
          <SliderArrowComponent
            button={'next'}
            classSize={'h-[80px] w-[80px]'}
            classSizeIcon={'w-[34px] h-[45px]'}
            className={'hover:!border-secondary hover:!bg-secondary'}
            onClick={() => {
              swiper?.slideNext()
              secondSwiper?.slideNext()
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default SliderMethodologyComponent
